/**
 * Created by Maxim B. on 01/04/20.
 * Copyright © 2020 SEVEN. All rights reserved.
 */
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output, ViewChild, ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Validators } from '@angular/forms';
import {MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS} from '@angular/material-moment-adapter';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';

export const DATE_FORMATS = {
  parse: {
    dateInput: 'DD MMM YYYY',
  },
  display: {
    dateInput: 'DD MMM YYYY',
    monthYearLabel: 'MMM YYYY',
    dateAllyLabel: 'DD MMM YYYY',
    monthYearAllyLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'curr-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (translateService: TranslateService) => {
        // Map the application's language code to the appropriate locale code
        const lang = translateService.currentLang || 'en';
        return lang === 'ch' ? 'zh-cn' : lang;
      },
      deps: [TranslateService]
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS},
  ],
})
export class DatepickerComponent implements OnInit, OnChanges {

  @ViewChild('customInput', { read: ViewContainerRef, static: true }) public customInput: ViewContainerRef;
  @Input() isBordered = true;
  @Input() placeholder = '';
  @Input() value: string | number;
  @Input() controlName: string;
  @Input() errorMessage = '';
  @Input() prefix = '';
  @Input() isGreyBackground = false;
  @Input() isDisabled = false;
  @Input() isReadOnly = false;
  @Input() formGroup: FormGroup;
  @Input() isCentered?: boolean;
  @Input() isRequired?: boolean;
  @Input() suffix?: string;
  @Input() isSuffixDisabled: boolean = false;
  @Input() userHint = '';
  @Input() activeFromToday  = false;
  @Output() valueChange = new EventEmitter<string | number | null>();
  @Output() onInputBlur = new EventEmitter<string | number | null>();
  @Output() onSuffixClick = new EventEmitter<void>();

  constructor(
    private fb: FormBuilder) {
  }

  daysFilter = (date: Date | null): boolean => {
    if (this.activeFromToday) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date && date >= today;
    }
    return true;
  };

  ngOnInit(): void {
    if (!this.formGroup) {
      this.controlName = 'item' + Date.now() + Math.random();
      this.formGroup = this.fb.group({
        [this.controlName]: new FormControl({ value: this.value, disabled: this.isDisabled })
      });
    }

    // Set validation rules
    if (this.isRequired) {
      this.formGroup.controls[this.controlName].setValidators([Validators.required]);
    }
  }

  changeValue(val: any) {
    this.valueChange.emit(val);
  }

  updateValue() {
    if (this.formGroup.controls[this.controlName] && !this.isReadOnly) {
      const val = this.formGroup.controls[this.controlName].value;
      this.valueChange.emit(val);
    }
  }

  ngOnChanges(): void {
    if (this.formGroup && this.value?.toString().length) {
      this.formGroup.controls[this.controlName].setValue(this.value);
    }
    if (this.formGroup) {
      if (this.isDisabled) {
        this.formGroup.controls[this.controlName].disable();
      } else {
        this.formGroup.controls[this.controlName].enable();
      }
    }
  }

  onLostFocus() {
    if (this.formGroup.controls[this.controlName]) {
      const val = this.formGroup.controls[this.controlName].value;
      this.onInputBlur.emit(val);
    }
  }

  suffixClicked(event): void {
    if (this.isSuffixDisabled) {
      return;
    }
    event.stopPropagation();
    this.onSuffixClick.emit();
  }

  focusInput(): void {
    this.customInput.element.nativeElement.focus();
  }
}
